import React, { FC, useContext, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { ChartType } from 'chart.js'

import AnalyticsChart from '../components/AnalyticsChart'
import AnalyticsHourChart from '../components/AnalyticsHourChart'
import AnalyticsPieChart from '../components/AnalyticsPieChart'
import {
  graphOptions,
  hourlyGraphOptions,
  pieGraphOptions
} from '../chartOptions'
import Loader from '../../../components/common/loader/Loader'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { TableExtendedHeader } from '../../../components/common/table-extended-old/table-extended-header/TableExtendedHeader'
import { AnalyticsHighlight } from '../AnalyticsHighlight'
import { SimpleLineIconsTypes } from '../../../components/common/IconFont'
import PeopleOverviewTable from './PeopleOverviewTable'
import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { usePeopleAnalyticsQuery } from './People.codegen'
import { TooltipHelpQuestion } from '../../../components/common/tooltip/TooltipHelp'
import { AnalyticsContext } from '../AnalyticsContext'

interface IProps {
  showFilterPanel?: boolean
}

const People: FC<IProps> = ({ showFilterPanel }: IProps) => {
  const { filterAsQueryVariables, changeFilter } = useContext(AnalyticsContext)
  if (filterAsQueryVariables.pathwayIds?.length) {
    /* Filters used in other analytics sections are passed via URL across all analytics section.
    This is fine and expected behaviour, however People section and User detail section should not accept pathwayIds filter value
    as the UI is not prepared for pathwayIds filter at the moment.
    Therefore, even the code should not accept such filter and pass it to the backend.
    This is quick hackfix that removes pathwayIds filter from URL when user
    accesses People/UserDetail section to avoid displaying invalid data.*/
    changeFilter({ pathwayIds: null })
  }
  // The same above applies also to the filter includeDrafts
  if (filterAsQueryVariables.includeDrafts) {
    changeFilter({ includeDrafts: false })
  }

  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )

  const { data, loading } = usePeopleAnalyticsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: filterAsQueryVariables,
      chartTimeUnit
    }
  })

  if (loading || !data?.analytics.resources) {
    return <Loader />
  }

  const {
    analytics: {
      resources: {
        totalViewsCountAllDevices,
        totalViewsCountMobile,
        totalViewsCountTablet,
        topicViewsByHour,
        topicViewsByTimeUnit
      },
      people
    }
  } = data

  const { activeUsersByDay, uniqueActiveUsersCount } = people
  const totalViewsDesktop =
    totalViewsCountAllDevices - totalViewsCountTablet - totalViewsCountMobile
  const viewsPerPerson =
    Math.round((totalViewsCountAllDevices / uniqueActiveUsersCount) * 10) / 10

  const deviceTypesData = [
    {
      value: totalViewsDesktop,
      label: t`Desktop`,
      icon: 'screen-desktop' as SimpleLineIconsTypes,
      color: '#c2daf4',
      percentage: totalViewsCountAllDevices
        ? Math.round((totalViewsDesktop / totalViewsCountAllDevices) * 100)
        : 0
    },
    {
      value: totalViewsCountTablet,
      label: t`Tablet`,
      icon: 'screen-tablet' as SimpleLineIconsTypes,
      color: '#86b6e9',
      percentage: totalViewsCountAllDevices
        ? Math.round((totalViewsCountTablet / totalViewsCountAllDevices) * 100)
        : 0
    },
    {
      value: totalViewsCountMobile,
      label: t`Mobile`,
      icon: 'screen-smartphone' as SimpleLineIconsTypes,
      color: '#4790dd',
      percentage: totalViewsCountAllDevices
        ? Math.round((totalViewsCountMobile / totalViewsCountAllDevices) * 100)
        : 0
    }
  ]

  const renderAnalyticsBoxes = () => {
    return (
      <>
        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={uniqueActiveUsersCount}
                icon="people"
                description={
                  <>
                    <Trans>Active people</Trans>
                    <TooltipHelpQuestion>
                      <Trans>
                        Number of people who have interacted with e.g.
                        started/completed a resource/pathway/certificate/survey
                        or attended an event.
                      </Trans>
                    </TooltipHelpQuestion>
                  </>
                }
                iconColor="green"
              />
              <AnalyticsHighlight
                value={totalViewsCountAllDevices}
                icon="eye"
                description={t`Resource views`}
                iconColor="blue"
              />
              <AnalyticsHighlight
                value={viewsPerPerson}
                icon="reload"
                description={t`Views per person`}
                iconColor="purple"
              />
            </div>
            <AnalyticsChart
              width={800}
              height={160}
              config={{
                type: 'line' as ChartType,
                options: graphOptions
              }}
              data={[
                { dataset: activeUsersByDay },
                { dataset: topicViewsByTimeUnit, color: '11,146,213' }
              ]}
              onGranularityChanged={setChartTimeUnit}
              granularity={chartTimeUnit}
            />
          </BorderedBox>
        </div>

        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Device types`} />
            <AnalyticsPieChart
              config={{
                type: 'pie' as ChartType,
                options: pieGraphOptions
              }}
              width={110}
              height={110}
              legendData={deviceTypesData}
              showLegend={true}
              className="Analytics__devicesChart"
            />
          </BorderedBox>

          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Activity time`} />
            <div className="Analytics__hourChart">
              <AnalyticsHourChart
                width={400}
                height={100}
                config={{
                  type: 'roundedBar' as ChartType,
                  options: hourlyGraphOptions
                }}
                data={topicViewsByHour}
              />
            </div>
          </BorderedBox>
        </div>
      </>
    )
  }

  return (
    <>
      <AnalyticsHeader showFilterPanel={showFilterPanel}>
        <Trans>People</Trans>
      </AnalyticsHeader>
      {renderAnalyticsBoxes()}
      <PeopleOverviewTable />
    </>
  )
}

export default People
